import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Bell, Send, Eye, Radio, TrendingUp, Users, Zap,
  RefreshCw, ArrowRight, Activity, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config/api';

const CHANNEL_COLORS = {
  pos: { bg: 'bg-blue-500/10', text: 'text-blue-400', bar: 'bg-blue-500', accent: '#3b82f6' },
  kds: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', bar: 'bg-indigo-500', accent: '#6366f1' },
  admin: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', bar: 'bg-emerald-500', accent: '#10b981' },
  admins: { bg: 'bg-violet-500/10', text: 'text-violet-400', bar: 'bg-violet-500', accent: '#8b5cf6' },
  order: { bg: 'bg-orange-500/10', text: 'text-orange-400', bar: 'bg-orange-500', accent: '#f97316' },
  expo: { bg: 'bg-teal-500/10', text: 'text-teal-400', bar: 'bg-teal-500', accent: '#14b8a6' },
  timeclock: { bg: 'bg-green-500/10', text: 'text-green-400', bar: 'bg-green-500', accent: '#22c55e' },
  staff: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', bar: 'bg-cyan-500', accent: '#06b6d4' },
  general: { bg: 'bg-gray-500/10', text: 'text-gray-400', bar: 'bg-gray-500', accent: '#6b7280' },
};

export default function NotificationAnalyticsPage() {
  const { token } = useOutletContext();
  const [summary, setSummary] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelDetail, setChannelDetail] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      // The company admin (super admin) reads the whole platform: every restaurant's rows.
      const { data } = await axios.get(`${API}/notifications/analytics/summary`, { headers });
      setSummary(data);
      const { data: feed } = await axios.get(`${API}/notifications/analytics/activity?limit=20`, { headers });
      setActivity(feed.events || []);
      setLoadError('');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setLoadError((typeof detail === 'string' && detail) || `Failed to load analytics${status ? ` (HTTP ${status})` : ''}`);
    }
    setLoading(false);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChannelDetail = useCallback(async (ch) => {
    try {
      const { data } = await axios.get(`${API}/notifications/analytics/channels/${ch}`, { headers });
      setChannelDetail(data);
      setSelectedChannel(ch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load channel details');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => { loadSummary(); }, [loadSummary]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center border border-red-200 bg-red-50 rounded-xl p-6" data-testid="noti-load-error">
        <p className="font-semibold text-red-700">Notification analytics could not be loaded</p>
        <p className="text-sm text-red-600 mt-1">{loadError}</p>
        <Button size="sm" variant="outline" className="mt-4" onClick={loadSummary}>Retry</Button>
      </div>
    );
  }

  const maxSent = summary?.channels?.length
    ? Math.max(...summary.channels.map(c => c.sent), 1)
    : 1;

  return (
    <div className="space-y-6" data-testid="notification-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600" /> Notification Analytics
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {summary?.restaurant_id ? `In-app notifications for restaurant ${summary.restaurant_id}, by channel` : 'In-app notifications across all restaurants, by channel'}
          </p>
          {summary?.note && <p className="text-xs text-amber-700 mt-1" data-testid="noti-sent-meaning">{summary.note}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadSummary} data-testid="noti-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <SummaryCard icon={Send} label="Total Sent" value={summary?.total_sent || 0}
          color="text-blue-600" bg="bg-blue-50" />
        <SummaryCard icon={Eye} label="Total Opened" value={summary?.total_opened || 0}
          color="text-emerald-600" bg="bg-emerald-50" />
        <SummaryCard icon={TrendingUp} label="Open Rate" value={`${summary?.open_rate || 0}%`}
          color="text-amber-600" bg="bg-amber-50" />
        <SummaryCard icon={Users} label="Subscribers" value={summary?.total_subscribers || 0}
          color="text-violet-600" bg="bg-violet-50" />
        <SummaryCard icon={Radio} label="Channels" value={summary?.total_channels || 0}
          color="text-cyan-600" bg="bg-cyan-50" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Channel Breakdown */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-gray-400" /> Channel Performance
          </h3>
          <div className="space-y-3">
            {summary?.channels?.map(ch => {
              const colors = CHANNEL_COLORS[ch.channel] || CHANNEL_COLORS.general;
              const barWidth = Math.max((ch.sent / maxSent) * 100, 2);
              return (
                <button key={ch.channel} onClick={() => loadChannelDetail(ch.channel)}
                  className={`w-full text-left p-3 rounded-lg border transition-all hover:shadow-sm ${
                    selectedChannel === ch.channel ? 'border-blue-300 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200'
                  }`}
                  data-testid={`noti-ch-${ch.channel}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${colors.bar}`} />
                      <span className="font-medium text-sm text-gray-800">{ch.name}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        {ch.subscribers} sub{ch.subscribers !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-gray-500">{ch.sent} sent</span>
                      <span className={ch.open_rate > 50 ? 'text-emerald-600 font-semibold' : 'text-gray-500'}>
                        {ch.open_rate}% open
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-300" />
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${colors.bar} transition-all duration-500`}
                      style={{ width: `${barWidth}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Channel Detail or Type Breakdown */}
          {channelDetail ? (
            <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="noti-ch-detail">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{channelDetail.name}</h3>
                <button onClick={() => { setSelectedChannel(null); setChannelDetail(null); }}
                  className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              </div>
              <p className="text-xs text-gray-500 mb-3">{channelDetail.description}</p>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-900">{channelDetail.total_sent}</p>
                  <p className="text-[10px] text-gray-500">Sent</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-emerald-600">{channelDetail.open_rate}%</p>
                  <p className="text-[10px] text-gray-500">Open Rate</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className="text-lg font-bold text-gray-900">{channelDetail.subscribers}</p>
                  <p className="text-[10px] text-gray-500">Subscribers</p>
                </div>
              </div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                <Zap className="w-3 h-3 inline mr-1" />Topic Breakdown
              </h4>
              <div className="space-y-2">
                {channelDetail.topics?.map(t => (
                  <div key={t.topic} className="flex items-center justify-between text-xs py-1">
                    <span className="text-gray-700">{t.topic.replace(/_/g, ' ')}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">{t.sent}</span>
                      <span className={t.open_rate > 50 ? 'text-emerald-600 font-medium' : 'text-gray-500'}>
                        {t.open_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3">By Type</h3>
              <div className="space-y-2">
                {Object.entries(summary?.by_type || {}).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between text-sm py-1.5">
                    <span className="text-gray-600 capitalize">{type.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Feed */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-gray-400" /> Recent Activity
            </h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {activity.length === 0 && (
                <p className="text-gray-400 text-sm text-center py-4">No recent activity</p>
              )}
              {activity.map((e, i) => {
                const colors = CHANNEL_COLORS[e.channel] || CHANNEL_COLORS.general;
                const actionColors = {
                  sent: 'text-blue-500', opened: 'text-emerald-500', dismissed: 'text-gray-400',
                };
                return (
                  <div key={i} className="flex items-center gap-2 text-xs py-1.5 border-b border-gray-50 last:border-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${colors.bar} shrink-0`} />
                    <span className="text-gray-500 truncate flex-1">
                      <span className={actionColors[e.action] || 'text-gray-500'}>{e.action}</span>
                      {' '}{e.topic?.replace(/_/g, ' ') || 'notification'}
                    </span>
                    <span className={`px-1 py-0.5 rounded text-[10px] ${colors.bg} ${colors.text}`}>{e.channel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`noti-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500">{label}</span>
        <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}
